import React, { FC, useMemo } from 'react';
import { SloQualityResultItemDTO } from '@atlassiansox/phobos-client-ts';
import BulletListIcon from '@atlaskit/icon/glyph/bullet-list';
import pluralize from 'pluralize';

import { PopupLink, ExternalLinkAction, KeyValue } from '@/components/primitive';
import { DAC_SLO_QUALITY_LINK } from '@/constants';
import { flexCenter, flexStart, spaceGapMinimum, spaceGapRelaxed, spaceTopCozy } from '@/styles';

import * as stylesSummaryHelpers from '../styles';

import { QualityScoreCheckProps } from './types';
import SloQualityLozenge from './slo-quality-lozenge';
import * as styles from './styles';
import QualityScoreCheckResults from './quality-score-check-results';
import { qualityScoreHelperDetailsTitle, testIds } from './constants';

const QualityScoreHelper: FC<QualityScoreCheckProps> = ({ quality, refSummary }) => {
  const sloQualityIssueCount = useMemo(() => {
    if (!quality || !quality.sloQualityResultItems) {
      return 0;
    }

    return quality.sloQualityResultItems.filter(
      (item: SloQualityResultItemDTO) => item.hasQualityIssue
    ).length;
  }, [quality]);

  const sloQualityWarningCount = useMemo(() => {
    if (!quality || !quality.sloQualityResultItems) {
      return 0;
    }

    return quality.sloQualityResultItems.filter(
      (item: SloQualityResultItemDTO) => item.metaData?.warning
    ).length;
  }, [quality]);

  if (!quality) return <></>;

  return (
    <PopupLink
      onToggle={() => {
        try {
          refSummary.current?.scrollIntoView({ behavior: 'smooth' });
        } catch (e) {
          // for any reason, if the ref is not available, we don't want to break the app
        }
      }}
      title="Show details"
      icon={
        <span css={styles.showDetailsIconStyles}>
          <BulletListIcon size="small" label="show details icon" />
        </span>
      }
    >
      <div css={styles.sloQualityDetailViewStyles}>
        <h5>{qualityScoreHelperDetailsTitle}</h5>
        <div css={[styles.infoBarStyles, flexStart, spaceGapRelaxed, spaceTopCozy]}>
          <KeyValue title="quality score">
            <span css={[flexCenter, spaceGapMinimum]} data-testid={testIds.qualityScoreHelperScore}>
              {quality.qualityScore === 10 && (
                <SloQualityLozenge appearance="success" isBold>
                  10
                </SloQualityLozenge>
              )}
              {quality.qualityScore && quality.qualityScore < 10 && (
                <SloQualityLozenge appearance="removed" isBold>
                  {quality.qualityScore}
                </SloQualityLozenge>
              )}
              <img alt="/" src="/assets/slash.svg" />
              <SloQualityLozenge>{10}</SloQualityLozenge>
            </span>
          </KeyValue>
          <KeyValue title="severity">
            <SloQualityLozenge>{quality.severity}</SloQualityLozenge>
          </KeyValue>
          {sloQualityIssueCount > 0 && (
            <KeyValue title={pluralize('issue', sloQualityIssueCount)}>
              <SloQualityLozenge appearance="moved">{sloQualityIssueCount}</SloQualityLozenge>
            </KeyValue>
          )}
          {sloQualityWarningCount > 0 && (
            <KeyValue title={pluralize('warning', sloQualityWarningCount)}>
              <SloQualityLozenge appearance="moved">{sloQualityWarningCount}</SloQualityLozenge>
            </KeyValue>
          )}
        </div>
        <QualityScoreCheckResults quality={quality} refSummary={refSummary} />
        <div css={[stylesSummaryHelpers.faqStyles, spaceTopCozy]}>
          <p>
            <b>FAQs</b>
          </p>
          <p>
            What is SLO quality and what categories are we checking?
            <ExternalLinkAction text="" href={`${DAC_SLO_QUALITY_LINK}#slo-quality`} />
          </p>
        </div>
      </div>
    </PopupLink>
  );
};

export default QualityScoreHelper;
